/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useProductsCategories, useProductsTypeProduct } from "../lib/react-query/useProducts";
import { useConfig } from "../lib/react-query/useConfig";
import { queryKeys } from "../lib/react-query/queryKeys";
import { useBooking } from "./useBooking";
import usePluginUtils from "./usePluginUtils";

export const usePreparedProducts = ({
  search = "",
  orderField = "",
  orderDirection = "",
  categoryId = null,
  forSale = false,
  enabled = true,
} = {}) => {
  const queryClient = useQueryClient();
  const lastCheckedIdsKeyRef = useRef(null);
  const { selectedServices } = useBooking();
  const { data: config } = useConfig();
  const { isPluginEnabled } = usePluginUtils();
  const productsAvailable = enabled && isPluginEnabled("product");
  const productsWithCategories = config?.product_use_categories;
  const {
    isLoading: loadingCategories,
    isFetching: fetchingCategories,
    data: categoriesData,
  } = useProductsCategories({ enabled: Boolean(productsAvailable && productsWithCategories) });
  const productsEnabled = Boolean(
    productsAvailable && (!forSale || productsWithCategories === false || !!categoryId)
  );
  const {
    isLoading: loadingProducts,
    isFetching: fetchingProducts,
    data: productsInfiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProductsTypeProduct({ search, orderField, orderDirection, categoryId, standaloneOnly: forSale, enabled: productsEnabled });
  const isLoading = loadingCategories || loadingProducts;
  const isFetching = fetchingCategories || fetchingProducts;

  const productsData = useMemo(
    () => productsInfiniteData?.pages?.flatMap((page) => page) ?? [],
    [productsInfiniteData]
  );

  const products = useMemo(() => {
    if (!productsData.length) return [];

    if (forSale) return [productsData];

    if (!selectedServices?.flat()?.length) {
      return [productsData.filter((product) => product.services?.length === 0)];
    }

    return selectedServices.map((service) =>
      productsData
        .filter(
          (product) => product.services?.some((s) => +s.service_id === +service.id) || product.services?.length === 0
        )
        .map((product) => {
          const selectedService = selectedServices.find((s) => +s.id === +service.id);
          const isProductSelected = selectedService?.products?.some((p) => p.id === product.id) ?? false;
          const matchedService = product.services?.find((s) => +s.service_id === +service.id);
          const qty = isProductSelected
            ? selectedService.products.find((p) => p.id === product.id).qty
            : +matchedService?.qty || 0;
          return { ...product, is_available: true, qty };
        })
    );
  }, [productsData, selectedServices, forSale]);

  const categoriesWithProducts = useMemo(() => {
    if (!productsWithCategories || !categoriesData) return [];
    return categoriesData.map((category) => ({
      ...category,
      productIds: category.products,
      products: productsData.filter((product) => category.products.some((id) => +id === +product.id)),
    }));
  }, [categoriesData, productsData, productsWithCategories]);

  useEffect(() => {
    if (!categoriesData || !productsEnabled || isLoading || isFetchingNextPage || hasNextPage) return;

    const relevantCategory = categoryId ? categoriesData.find((cat) => +cat.id === +categoryId) : null;
    if (!relevantCategory) return;

    const categoryProductIds = relevantCategory.products.map((id) => +id);
    const idsKey = [...categoryProductIds].sort((a, b) => a - b).join(",");
    if (idsKey === lastCheckedIdsKeyRef.current) return;

    const loadedIds = new Set(productsData.map((p) => +p.id));
    const hasMissing = categoryProductIds.some((id) => !loadedIds.has(id));

    lastCheckedIdsKeyRef.current = idsKey;
    if (hasMissing) {
      queryClient.resetQueries({
        queryKey: queryKeys.products.list({ search, orderField, orderDirection, categoryId }),
      });
    }
  }, [categoriesData, productsData, productsEnabled, isLoading, isFetchingNextPage, hasNextPage]);

  return {
    products,
    categoriesWithProducts,
    isLoading,
    isFetching,
    categoriesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};
